import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Phone, MapPin, Clock, ArrowRight, Check, Users, Package, Truck, MessageCircle } from "lucide-react";

/**
 * SEO-optimised public landing page per pickup location.
 * URL: /standort/:slug    (slug = st-gallen | st-gallen-wiesental | bern | derendingen | bronschhofen)
 *
 * Goals:
 *   - rank on local Google queries ("Vito mieten Bern", "Transporter Solothurn", …)
 *   - per-page <title>, <h1>, hreflang-friendly description, LocalBusiness JSON-LD
 *   - deep-link to /rent with the right station pre-selected
 *
 * NOTE: This page is intentionally NOT translated through i18n — it's keyword-optimised
 * German content because the search audience is Swiss/German-speaking. Add language
 * variants later if you want to rank for "Vito Mieten Bern English" etc.
 */

const LOCATIONS = {
  "st-gallen": {
    id: "st_gallen",
    name: "St. Gallen – Oberstrasse",
    cityHeadline: "St. Gallen",
    address: "Oberstrasse 222, 9014 St. Gallen",
    postal: "9014",
    phone: "+41793966660",
    vehicles: ["vito", "master"],
    hours: "Mo–Di 08:00–20:00, Do–So 08:00–20:00 (Mittwoch geschlossen)",
    intro:
      "Transporter mieten in St. Gallen — direkt an der Oberstrasse. Mercedes Vito 9-Plätzer für Personentransporte und Renault Master für Umzüge & Lieferungen. Vollkasko inklusive, ohne km-Limit.",
    keywords: "Transporter mieten St. Gallen, Vito mieten St. Gallen, 9-Plätzer mieten St. Gallen, Umzugswagen St. Gallen, Lieferwagen mieten St. Gallen",
    mapsQuery: "Oberstrasse 222, 9014 St. Gallen",
    faqs: [
      { q: "Wo befindet sich NIVARA in St. Gallen?", a: "Oberstrasse 222, 9014 St. Gallen — direkt mit dem Auto erreichbar, Parkplätze vor Ort." },
      { q: "Welche Fahrzeuge gibt es am Standort St. Gallen?", a: "Mercedes Vito 9-Plätzer (ab CHF 190.– pro Tag) und Renault Master Kastenwagen (ab CHF 80.– für 3 Stunden)." },
      { q: "Brauche ich einen Führerausweis Kat. B für den Vito?", a: "Ja, der reguläre PW-Führerausweis (Kat. B) reicht für beide Fahrzeuge bis 3.5 t." },
    ],
  },
  "st-gallen-wiesental": {
    id: "st_gallen_wiesental",
    name: "St. Gallen – Wiesental",
    cityHeadline: "St. Gallen Wiesental",
    address: "Wiesentalstrasse 6, 9000 St. Gallen",
    postal: "9000",
    phone: "+41793966660",
    vehicles: ["vito"],
    hours: "Mo–Di 08:00–20:00, Do–So 08:00–20:00",
    intro:
      "Vito 9-Plätzer mieten in St. Gallen Wiesental — ideal für Familienfeiern, Vereinsausflüge und Hochzeitsfahrten. Vollkasko inklusive, kein km-Limit.",
    keywords: "Vito mieten St. Gallen Wiesental, 9-Plätzer Wiesental, Kleinbus mieten St. Gallen",
    mapsQuery: "Wiesentalstrasse 6, 9000 St. Gallen",
    faqs: [
      { q: "Wo ist NIVARA Wiesental?", a: "Wiesentalstrasse 6, 9000 St. Gallen." },
      { q: "Kann ich den Vito ins Ausland mitnehmen?", a: "Ja, der Tarif für EU-Reisen beträgt ab CHF 230.– pro Tag." },
    ],
  },
  bern: {
    id: "bern",
    name: "Bern",
    cityHeadline: "Bern",
    address: "Libellenweg 21, 3006 Bern",
    postal: "3006",
    phone: "+41793966660",
    vehicles: ["master"],
    hours: "Mo–Di 08:00–20:00, Do–So 08:00–20:00",
    intro:
      "Renault Master mieten in Bern — der ideale Kastenwagen für Umzüge, Möbeltransporte und Lieferungen in und um Bern. 3-Stunden-Paket ab CHF 80.–.",
    keywords: "Transporter mieten Bern, Renault Master Bern, Umzugswagen Bern, Lieferwagen mieten Bern, Kastenwagen mieten Bern",
    mapsQuery: "Libellenweg 21, 3006 Bern",
    faqs: [
      { q: "Wo befindet sich NIVARA Bern?", a: "Libellenweg 21, 3006 Bern — am Stadtrand mit guter Anbindung an A1/A6." },
      { q: "Welche Fahrzeuge gibt es in Bern?", a: "Aktuell Renault Master (Kastenwagen, bis 1500 kg Nutzlast)." },
      { q: "Wie viel kostet ein Master für einen Umzug?", a: "3 Stunden ab CHF 80.–, 6 Stunden ab CHF 140.–, 10 Stunden ab CHF 200.– — inkl. Kilometer-Paket." },
    ],
  },
  derendingen: {
    id: "derendingen",
    name: "Derendingen",
    cityHeadline: "Solothurn / Derendingen",
    address: "Obergadenweg 1, 4552 Derendingen",
    postal: "4552",
    phone: "+41793966660",
    vehicles: ["master"],
    hours: "Mo–Di 08:00–20:00, Do–So 08:00–20:00",
    intro:
      "Renault Master Kastenwagen mieten in Derendingen / Solothurn — perfekt für Umzüge, Werkzeug- und Möbeltransporte im Mittelland. Vollkasko inklusive.",
    keywords: "Transporter mieten Solothurn, Lieferwagen Derendingen, Umzugswagen Solothurn, Renault Master Solothurn, Kastenwagen Derendingen",
    mapsQuery: "Obergadenweg 1, 4552 Derendingen",
    faqs: [
      { q: "Wo ist NIVARA in der Region Solothurn?", a: "Obergadenweg 1, 4552 Derendingen — 5 Minuten von Solothurn entfernt." },
      { q: "Kann ich den Master für nur 3 Stunden mieten?", a: "Ja, das Kurzpaket mit 3 Stunden inkl. Kilometern beginnt bei CHF 80.–." },
    ],
  },
  bronschhofen: {
    id: "bronschhofen",
    name: "Bronschhofen – Maugwilerstrasse",
    cityHeadline: "Wil / Bronschhofen",
    address: "Maugwilerstrasse 16, 9552 Bronschhofen",
    postal: "9552",
    phone: "+41793966660",
    vehicles: ["vito"],
    hours: "Mo–Di 08:00–20:00, Do–So 08:00–20:00 (Mittwoch geschlossen)",
    intro:
      "Mercedes Vito 9-Plätzer mieten in Bronschhofen — direkt an der Maugwilerstrasse 16. Ideal für Reisegruppen, Hochzeiten, Vereinsausflüge und Familienanlässe in Wil und der Region Toggenburg. Vollkasko inklusive, ohne km-Limit.",
    keywords: "Vito mieten Bronschhofen, 9-Plätzer mieten Wil, Kleinbus mieten Bronschhofen, Personentransporter Wil, Transporter mieten Toggenburg, Vito Maugwilerstrasse, Mercedes Vito mieten Wil SG, Hochzeitsauto Wil, Vereinsbus Bronschhofen",
    mapsQuery: "Maugwilerstrasse 16, 9552 Bronschhofen",
    faqs: [
      { q: "Wo befindet sich NIVARA in Bronschhofen?", a: "Maugwilerstrasse 16, 9552 Bronschhofen — direkt zwischen Wil und dem Toggenburg, gut erreichbar via A1 (Ausfahrt Wil) und mit kostenlosen Parkplätzen vor Ort." },
      { q: "Welche Fahrzeuge gibt es am Standort Bronschhofen?", a: "Mercedes Vito 9-Plätzer (ab CHF 190.– pro Tag) — perfekt für Familienausflüge, Hochzeiten, Vereinsfahrten und Reisegruppen bis 9 Personen." },
      { q: "Brauche ich einen speziellen Führerausweis für den Vito?", a: "Nein, der reguläre PW-Führerausweis Kategorie B reicht aus — der Vito 9-Plätzer wiegt unter 3.5 Tonnen." },
      { q: "Kann ich den Vito ins Ausland mitnehmen?", a: "Ja. Der EU-Tarif beträgt ab CHF 230.– pro Tag. Vollkasko und Pannenhilfe gelten europaweit." },
      { q: "Welche Regionen werden ab Bronschhofen bedient?", a: "Wil SG, Toggenburg, Hinterthurgau, Fürstenland und Region St. Gallen — Abholung in Bronschhofen, Rückgabe wahlweise auch in St. Gallen-Oberstrasse möglich." },
    ],
  },
};

const VEHICLE_INFO = {
  vito: {
    icon: Users,
    title: "Mercedes Vito · 9-Plätzer",
    desc: "Personentransporter für 9 Personen. Vollkasko inkl. CHF 1'000 Selbstbehalt.",
    pricing: "ab CHF 190.– / Tag (CH) · CHF 230.– / Tag (EU)",
    image: "/vito114.webp",
  },
  master: {
    icon: Package,
    title: "Renault Master · Kastenwagen",
    desc: "Warentransporter bis 1500 kg Nutzlast. Vollkasko inkl. CHF 1'000 Selbstbehalt.",
    pricing: "3h ab CHF 80.– · 6h ab CHF 140.– · 10h ab CHF 200.–",
    image: "/renault-master.webp",
  },
};

const LocationPage = () => {
  const { slug } = useParams();
  const loc = LOCATIONS[slug];

  // Inject per-page meta tags into document head for crawlers + social previews
  useEffect(() => {
    if (!loc) return;
    const title = `Transporter mieten in ${loc.cityHeadline} · NIVARA`;
    document.title = title;

    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", loc.intro);
    setMeta("keywords", loc.keywords);
    setMeta("og:title", title, "property");
    setMeta("og:description", loc.intro, "property");
    setMeta("og:url", `https://nivaragroup.ch/standort/${slug}`, "property");

    // LocalBusiness structured data injected per page
    const scriptId = "ld-local-business";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const ld = document.createElement("script");
    ld.id = scriptId;
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AutoRental",
      name: `NIVARA ${loc.name}`,
      description: loc.intro,
      url: `https://nivaragroup.ch/standort/${slug}`,
      telephone: loc.phone,
      priceRange: "CHF 80–1400",
      areaServed: ["CH"],
      openingHours: ["Mo-Tu 08:00-20:00", "Th-Su 08:00-20:00"],
      address: {
        "@type": "PostalAddress",
        streetAddress: loc.address.split(",")[0].trim(),
        postalCode: loc.postal,
        addressLocality: loc.cityHeadline,
        addressCountry: "CH",
      },
    });
    document.head.appendChild(ld);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [slug, loc]);

  if (!loc) return <Navigate to="/" replace />;

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="bg-[var(--rs-primary)] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 sm:py-20">
          <div className="rs-overline !text-white/60">Standort</div>
          <h1 className="font-[Chivo] text-4xl sm:text-6xl font-black tracking-tight mt-2 leading-[1]">
            Transporter mieten in {loc.cityHeadline}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/90 leading-relaxed">{loc.intro}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/?station=${loc.id}`}
              className="bg-white text-[var(--rs-primary)] hover:bg-[var(--rs-surface)] inline-flex items-center font-bold uppercase tracking-wide px-6 py-4 transition-colors"
              data-testid="location-cta-rent"
            >
              Jetzt online buchen <ArrowRight size={18} className="ml-2" />
            </Link>
            <a
              href={`tel:${loc.phone}`}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[var(--rs-primary)] inline-flex items-center font-bold uppercase tracking-wide px-6 py-4 transition-colors"
              data-testid="location-cta-call"
            >
              <Phone size={16} className="mr-2" /> 079 396 66 60
            </a>
          </div>
        </div>
      </section>

      {/* Adresse + Karte */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border-2 border-[var(--rs-text)] p-6">
            <div className="rs-overline mb-2 flex items-center gap-2"><MapPin size={14} /> Adresse</div>
            <div className="font-[Chivo] text-2xl font-black">{loc.name}</div>
            <div className="text-sm text-[var(--rs-text-muted)] mt-2">{loc.address}</div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.mapsQuery)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 text-xs uppercase tracking-[0.2em] text-[var(--rs-primary)] font-bold underline"
              data-testid="location-maps-link"
            >
              In Google Maps öffnen →
            </a>

            <div className="mt-6 rs-overline flex items-center gap-2"><Clock size={14} /> Öffnungszeiten</div>
            <div className="text-sm mt-1">{loc.hours}</div>

            <div className="mt-6 rs-overline flex items-center gap-2"><Phone size={14} /> Kontakt</div>
            <a href={`tel:${loc.phone}`} className="text-sm font-bold text-[var(--rs-primary)] underline">079 396 66 60</a>
            {" · "}
            <a
              href={`https://wa.me/41793966660?text=${encodeURIComponent(`Hallo NIVARA ${loc.cityHeadline}, ich habe eine Frage zur Vermietung.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-bold text-[#128C7E] underline inline-flex items-center gap-1"
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
          </div>

          <div className="overflow-hidden border-2 border-[var(--rs-border)]">
            <iframe
              title={`Karte ${loc.name}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(loc.mapsQuery)}&output=embed`}
              className="w-full h-full min-h-[280px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Verfügbare Fahrzeuge */}
      <section className="bg-[var(--rs-surface)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
          <div className="rs-overline">Verfügbare Fahrzeuge</div>
          <h2 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tight mt-2">
            Unsere Flotte in {loc.cityHeadline}
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {loc.vehicles.map((v) => {
              const info = VEHICLE_INFO[v];
              const Icon = info.icon;
              return (
                <div key={v} className="bg-white border-2 border-[var(--rs-border)]" data-testid={`location-vehicle-${v}`}>
                  <div className="aspect-[16/10] bg-[var(--rs-surface)] overflow-hidden relative">
                    <img src={info.image} alt={info.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 bg-[var(--rs-primary)] text-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] inline-flex items-center gap-1">
                      <Icon size={11} /> {info.title.split(" · ")[0]}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-[Chivo] text-2xl font-black">{info.title}</h3>
                    <p className="text-sm text-[var(--rs-text-muted)] mt-2">{info.desc}</p>
                    <div className="mt-4 text-sm font-bold text-[var(--rs-primary)]">{info.pricing}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="rs-overline">Warum NIVARA?</div>
        <h2 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tight mt-2">Klare Preise. Volle Versicherung. Online buchbar.</h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: Truck, title: "Vollgetankt", desc: "Übergabe und Rückgabe immer mit vollem Tank — keine Treibstoff-Überraschungen." },
            { icon: Check, title: "Vollkasko inkl.", desc: "Vollkasko-Versicherung mit CHF 1'000 Selbstbehalt im Preis enthalten." },
            { icon: Clock, title: "Online buchen", desc: "Buchung in unter 3 Minuten. Vertrag direkt als PDF + per E-Mail." },
          ].map((h) => {
            const I = h.icon;
            return (
              <div key={h.title} className="border-l-4 border-[var(--rs-primary)] pl-4">
                <I size={20} className="text-[var(--rs-primary)]" />
                <h3 className="font-[Chivo] text-xl font-black mt-2">{h.title}</h3>
                <p className="text-sm text-[var(--rs-text-muted)] mt-1 leading-relaxed">{h.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[var(--rs-surface)]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-14">
          <div className="rs-overline">Häufige Fragen</div>
          <h2 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tight mt-2">FAQ · {loc.cityHeadline}</h2>
          <div className="mt-8 space-y-4">
            {loc.faqs.map((f) => (
              <details key={f.q} className="bg-white border border-[var(--rs-border)] p-5 group">
                <summary className="cursor-pointer font-bold text-[var(--rs-primary)] list-none flex items-center justify-between">
                  <span>{f.q}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-[var(--rs-text)] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--rs-primary)] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 text-center">
          <h2 className="font-[Chivo] text-3xl sm:text-5xl font-black tracking-tight">
            Bereit für die Buchung in {loc.cityHeadline}?
          </h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">In wenigen Klicks zum Mietvertrag. Vollkasko inklusive, ohne Überraschungen.</p>
          <Link
            to={`/?station=${loc.id}`}
            className="mt-8 inline-flex items-center bg-white text-[var(--rs-primary)] hover:bg-[var(--rs-surface)] font-bold uppercase tracking-wide px-8 py-4 transition-colors"
            data-testid="location-cta-bottom"
          >
            Jetzt buchen <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--rs-border)] py-8 text-center text-xs text-[var(--rs-text-muted)] tracking-[0.25em] uppercase">
        NIVARA Group · St. Gallen · Bern · Solothurn ·{" "}
        <Link to="/" className="text-[var(--rs-primary)] underline">nivaragroup.ch</Link>
      </footer>
      <Footer />
    </div>
  );
};

export default LocationPage;
